import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
  Pin,
  useMap,
} from "@vis.gl/react-google-maps";
import './MapView.css';
import {
  EMPOWERMENT_PIN_COLORS,
  EMPOWERMENT_ZONE_TYPES,
  getEmpowermentZoneCategory,
  getEmpowermentZoneLabel,
} from "../../../CommonUtils/empowermentZones";

const DEFAULT_US_CENTER = { lat: 37.0902, lng: -95.7129 };
const DEFAULT_US_ZOOM = 4;
const RESULTS_ZOOM = 12;

const DEFAULT_MAP_ZOOM = 4; 
const SINGLE_MARKER_ZOOM = 15;

const getLatLng = (biz) => {
  if (biz.latitude && biz.longitude)
    return { lat: parseFloat(biz.latitude), lng: parseFloat(biz.longitude) };
  return DEFAULT_US_CENTER;
};

const getMapCenter = (businesses) => {
  for (const biz of businesses) {
    if (biz.latitude && biz.longitude) {
      return getLatLng(biz);
    }
  }
  return DEFAULT_US_CENTER;
};

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_MAPS_API_KEY;

/**
 * To support "Loading..." logic for Projected Credit in MapView,
 * you must pass a prop called taxCreditLoading which is an object keyed by prospectBusinessId,
 * where each value is true while loading, and false/undefined when not loading.
 * Example: <MapView ..., taxCreditLoading={{"BUSINESS_ID": true}} />
 */
const MapView = ({
  businesses = [],
  taxCreditData = {},
  taxCreditLoading = {},
  initialCenter,
  onMarkerSelect,
  selectedBusinessIds = [],
}) => {
  // "Pinned" marker (from click)
  const [selectedBiz, setSelectedBiz] = useState(null);
  // "Hovered" marker (from mouseover)
  const [hoveredBiz, setHoveredBiz] = useState(null);

  const businessesHaveCoords = businesses.some(
    (biz) => biz.latitude && biz.longitude
  );
  const center = businessesHaveCoords
    ? getMapCenter(businesses)
    : initialCenter || getMapCenter(businesses);
  const initialZoom = businessesHaveCoords ? RESULTS_ZOOM : DEFAULT_US_ZOOM;
  const mapKey = `${center.lat}-${center.lng}-${initialZoom}`;
  const infoWindowTimeoutRef = useRef();
  const selectedIds = useMemo(() => {
    const ids = new Set();
    (selectedBusinessIds || [])
      .filter(Boolean)
      .forEach((id) => ids.add(String(id)));
    return ids;
  }, [selectedBusinessIds]);

  const resolveBusinessId = (biz) =>
    biz?.prospectBusinessId || biz?.businessId || biz?.id || "";

  // Remove InfoWindow if business disappears
  useEffect(() => {
    if (
      selectedBiz &&
      !businesses.find(
        (b) =>
          (b.businessId || b.prospectBusinessId) ===
          (selectedBiz.businessId || selectedBiz.prospectBusinessId)
      )
    ) {
      setSelectedBiz(null);
    }
    if (
      hoveredBiz &&
      !businesses.find(
        (b) =>
          (b.businessId || b.prospectBusinessId) ===
          (hoveredBiz.businessId || hoveredBiz.prospectBusinessId)
      )
    ) {
      setHoveredBiz(null);
    }
  }, [businesses, selectedBiz, hoveredBiz]);

  // Helper for InfoWindow content
  function InfoContent(biz) {
    const projectedCredit =
      biz.prospectBusinessId && taxCreditLoading[biz.prospectBusinessId]
        ? "Loading..."
        : typeof (biz.prospectBusinessId &&
            taxCreditData[biz.prospectBusinessId]?.projectedCredit) !==
            "undefined"
        ? `$${Number(
            taxCreditData[biz.prospectBusinessId]?.projectedCredit
          ).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
        : typeof biz.projectedCredit !== "undefined" &&
          biz.projectedCredit !== null
        ? `$${Number(biz.projectedCredit).toLocaleString(undefined, {
            maximumFractionDigits: 2,
          })}`
        : "No Data";

    const zoneLabel = getEmpowermentZoneLabel(biz.empowermentZone);

    return (
      <div
        style={{
          minWidth: 260,
          fontFamily: "inherit",
          padding: 0,
          borderRadius: 18,
          fontWeight: 400,
        }}
      >
        <b style={{ fontSize: 18 }}>{biz.businessName}</b>
        <div style={{ color: "#555", marginBottom: 4, fontSize: 15 }}>
          {[biz.siC01Description, biz.physicalCity, biz.state]
            .filter(Boolean)
            .join(", ")}
        </div>
        {zoneLabel && (
          <div style={{ color: "#333", marginBottom: 4, fontSize: 14 }}>
            Empowerment Zone: <b>{zoneLabel}</b>
          </div>
        )}
        <div>
          Projected Credit: <b>{projectedCredit}</b>
        </div>
      </div>
    );
  }

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <Map
        key={mapKey}
        mapId="fd16af6e873de8bf6283afde"
        style={{ width: "100%", height: "100%", borderRadius: 18 }}
        defaultZoom={initialZoom}
        defaultCenter={center}
        gestureHandling="greedy"
        disableDefaultUI={false}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        }}
      >
        {businesses
          .filter((biz) => biz.latitude && biz.longitude)
          .map((biz, idx) => {
            const bizId = resolveBusinessId(biz);
            const isSelected =
              bizId && selectedIds.has(String(bizId));
            const category = getEmpowermentZoneCategory(biz.empowermentZone);
            const colors =
              EMPOWERMENT_PIN_COLORS[category] ||
              EMPOWERMENT_PIN_COLORS[EMPOWERMENT_ZONE_TYPES.NONE];
            return (
              <AdvancedMarker
              key={biz.businessId || idx}
              position={getLatLng(biz)}
              onClick={() => {
                setSelectedBiz(biz);
                setHoveredBiz(null);
                if (typeof onMarkerSelect === "function") {
                  onMarkerSelect(biz);
                }
              }}
              onMouseOver={() => {
                clearTimeout(infoWindowTimeoutRef.current);
                setHoveredBiz(biz);
              }}
              onMouseOut={() => {
                // Only remove hover after a short delay, to allow click
                infoWindowTimeoutRef.current = setTimeout(() => {
                  setHoveredBiz(null);
                }, 150);
              }}
              title={biz.businessName}
            >
              <Pin
                background={colors.background}
                borderColor={isSelected ? "#000000" : colors.borderColor}
                glyphColor={colors.glyphColor}
                scale={isSelected ? 1.4 : 1.1}
              />
              </AdvancedMarker>
            );
          })}

        {/* Hovered InfoWindow (takes precedence over selected) */}
        {hoveredBiz && hoveredBiz.latitude && hoveredBiz.longitude && (
          <InfoWindow
            position={getLatLng(hoveredBiz)}
            pixelOffset={{ x: 0, y: -10 }}
            onCloseClick={() => setHoveredBiz(null)}
            zIndex={2}
          >
            {InfoContent(hoveredBiz)}
          </InfoWindow>
        )}
        {/* Selected (clicked) InfoWindow, only shown if not hovering another marker */}
        {selectedBiz &&
          selectedBiz.latitude &&
          selectedBiz.longitude &&
          !hoveredBiz && (
            <InfoWindow
              position={getLatLng(selectedBiz)}
              pixelOffset={{ x: 0, y: -10 }}
              onCloseClick={() => setSelectedBiz(null)}
              zIndex={1}
            >
              {InfoContent(selectedBiz)}
            </InfoWindow>
          )}
        <MapBoundsFitter businesses={businesses} />
      </Map>
    </APIProvider>
  );
};

const MapBoundsFitter = ({ businesses = [] }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const withCoords = (businesses || []).filter(
      (biz) => biz.latitude && biz.longitude
    );

    if (!withCoords.length) {
      map.setCenter(DEFAULT_US_CENTER);
      map.setZoom(DEFAULT_MAP_ZOOM);
      return;
    }

    const boundsBuilder = window?.google?.maps?.LatLngBounds;
    if (!boundsBuilder) return;

    const bounds = new boundsBuilder();
    withCoords.forEach((biz) => bounds.extend(getLatLng(biz)));
    map.fitBounds(bounds);
    if (withCoords.length === 1) {
      map.setCenter(getLatLng(withCoords[0]));
      map.setZoom(SINGLE_MARKER_ZOOM);
    }
  }, [map, businesses]);

  return null;
};

export default MapView;
